import { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Broadcast, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const SEVERITIES = ["critical", "high", "warning", "info"];

export default function AlertEvents() {
  const [form, setForm] = useState({ source: "Datadog", severity: "critical", summary: "High CPU on prod-db-01", description: "APM detected sustained CPU > 95% for 5 minutes.", host: "prod-db-01", service: "database" });
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);

  const send = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/events", form);
      setCreated(data);
      toast.success(data.correlated ? `Correlated to existing ${data.number}` : `Ticket ${data.number} raised from alert`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6" data-testid="events-view">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Monitoring Integration</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black">Alert Webhook</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Simulate an inbound monitoring alert. In production, tools like Datadog / Prometheus POST to <span className="font-mono text-primary">/api/events</span>.
          Severity maps to priority (<span className="font-mono">critical→P1</span>, <span className="font-mono">high→P2</span>, <span className="font-mono">warning→P3</span>, <span className="font-mono">info→P4</span>). Repeated alerts with the same summary correlate to the open ticket.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border rounded-sm">
          <div className="flex items-center gap-2 mb-4">
            <Broadcast size={16} className="text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-widest">Alert Payload</span>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Source</Label>
                <Input data-testid="event-source" className="mt-1 rounded-sm font-mono" value={form.source} onChange={(e)=>setForm({...form, source: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Severity</Label>
                <Select value={form.severity} onValueChange={(v)=>setForm({...form, severity: v})}>
                  <SelectTrigger data-testid="event-severity" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Host</Label>
                <Input data-testid="event-host" className="mt-1 rounded-sm font-mono" value={form.host} onChange={(e)=>setForm({...form, host: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Service</Label>
                <Input data-testid="event-service" className="mt-1 rounded-sm font-mono" value={form.service} onChange={(e)=>setForm({...form, service: e.target.value})} />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Summary</Label>
              <Input data-testid="event-summary" className="mt-1 rounded-sm" value={form.summary} onChange={(e)=>setForm({...form, summary: e.target.value})} />
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Description</Label>
              <Textarea data-testid="event-description" className="mt-1 rounded-sm min-h-[120px]" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} />
            </div>
            <Button data-testid="send-event-btn" onClick={send} disabled={saving || !form.summary} className="w-full rounded-sm font-mono text-xs uppercase tracking-widest">
              {saving ? "Firing…" : "Fire Alert"}
            </Button>
          </div>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Result</div>
          {created ? (
            <div className="space-y-3">
              <div className="text-primary font-mono text-2xl">{created.number}</div>
              <div className="font-display text-lg">{created.title}</div>
              <div className="text-xs font-mono text-muted-foreground">Priority {created.priority} · {created.correlated ? "correlated (comment added)" : "new incident"}</div>
              <Link data-testid="open-created-event" to={`/incidents/${created.id}`} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary hover:underline">
                Open ticket <ArrowRight size={12} />
              </Link>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">No alert fired yet.</div>
          )}
        </Card>
      </div>
    </div>
  );
}
